// client/src/pages/sections/groups/clientRosterCache.js
// Module-level cache for /api/dgGroupAnchors. We only want the partner
// to pick from EXISTING SG / RG parent anchors when moving a client, so
// this cache holds the two distinct anchor lists rather than the full
// roster. Single in-flight request shared across all callers.
//
//   loadAnchors()  → Promise<{ sgAnchors, rgAnchors, rgOrphans }>
//   getAnchors()   → synchronous accessor; null until first load resolves
//   primeAnchors() → fire-and-forget warmup callable from section mount

import { rememberClientNames } from './ClientChip';

let cached = null;
let inflight = null;

// `fresh: true` bypasses the cached copy and refetches (the SG move picker uses it each time it
// opens: it only accepts a target from this list, so a group anchor created since the page loaded
// must be in it).
export function loadAnchors({ fresh = false } = {}) {
  if (cached && !fresh) return Promise.resolve(cached);
  if (inflight) return inflight;
  inflight = fetch('/api/dgGroupAnchors', { credentials: 'include' })
    .then(async (r) => {
      if (!r.ok) throw new Error(await r.text() || `HTTP ${r.status}`);
      const j = await r.json();
      rememberClientNames(j);
      cached = {
        sgAnchors: j.sgAnchors || [],
        rgAnchors: j.rgAnchors || [],
        // Ungrouped orphan clients — only MoveToRgButton merges these into
        // its RG picker (see dgGroupAnchors). Other pickers use rgAnchors.
        rgOrphans: j.rgOrphans || [],
      };
      return cached;
    })
    .catch((e) => {
      inflight = null;
      throw e;
    })
    .finally(() => { inflight = null; });
  return inflight;
}

export function getAnchors() {
  return cached;
}

export function primeAnchors() {
  loadAnchors().catch(() => { /* swallow — picker will retry on open */ });
}
